import { filterTargetBuilds } from '@common/lib/filterTargetBuilds.ts';
import {
  type BuyCoinItemProps,
  type StorageItemProps,
} from '@common/types/components';
import { type GoodsType } from '@common/types/goods';
import { type PlantTypeText } from '@common/types/plant';
import { type ResourceTypeText } from '@common/types/resource';
import {
  type Balance,
  type MaterialDetailsType,
  type NextBuild,
  type PlantDataType,
  type QuarryNextLevel,
  type Storage,
  type TgemCost,
  type UnionGameItemType,
  type User,
} from '@common/types/userType';
import { type RootState } from '@redux/store';
import { createSelector } from '@reduxjs/toolkit';
import Decimal from 'decimal.js';

import {
  type PaidSpecContractType,
  type SpecContractType,
} from '@/common/types/contract';
import {
  type LotteryTicketType,
  type ShortLotterySeasonInfo,
} from '@/common/types/lottery';
import { type PaidVipCostObjectType } from '@/common/types/query/user';
import {
  type BalanceItem,
  type FilteredHoldedBalanceItem,
} from '@/common/types/user';

const selectStorage = (state: RootState): Storage | undefined =>
  state.user.user?.storage;

const selectPlantsData = (state: RootState): PlantDataType | undefined =>
  state.user.user?.plants;

const holdedBalance = (state: RootState): Balance | undefined =>
  state.user.user?.holdedBalance;

const stepPlantsToGetNextLevel = (
  state: RootState,
): QuarryNextLevel[] | undefined =>
  state.user.extendedInfo?.stepPlantsNextLevel;

const selectResourceStorageByName = createSelector(
  selectStorage,
  (
    _,
    resourceName?: ResourceTypeText | PlantTypeText | GoodsType,
  ): ResourceTypeText | PlantTypeText | GoodsType | undefined => resourceName,
  (
    storage: Storage | undefined,
    resourceName: ResourceTypeText | PlantTypeText | GoodsType | undefined,
  ): number | undefined => {
    if (storage && resourceName) {
      return resourceName in storage && storage[resourceName]
        ? storage[resourceName].value
        : undefined;
    } else {
      return undefined;
    }
  },
);

const selectStorageItemBySample = createSelector(
  selectStorage,
  (_, sample: string[]): string[] => sample,
  (
    storage: Storage | undefined,
    sample: string[],
  ): StorageItemProps[] | void => {
    if (storage) {
      const filteredSet = new Set(sample);

      return Object.entries(storage)
        .filter(([key]) => filteredSet.has(key))
        .map(([key, value]) => {
          const detail = value as MaterialDetailsType;

          return {
            gameItem: key as ResourceTypeText | PlantTypeText | GoodsType,
            costPerUnit: detail.costPerUnit,
            value: detail.value,
            storageLimit: detail.storageLimit,
          };
        });
    }
  },
);

const selectIsGameItemHasOverflow = createSelector(
  selectStorage,
  (_, sample: string[]): string[] => sample,
  (storage: Storage | undefined, sample: string[]): boolean => {
    const filteredSet = new Set(sample);

    return storage
      ? Object.entries(storage)
          .filter(([key]) => filteredSet.has(key))
          .some(([, value]) => {
            const detail = value as MaterialDetailsType;

            return detail.storageLimit
              ? detail.value > detail.storageLimit
              : false;
          })
      : false;
  },
);

const selectBuildLevelByName = createSelector(
  selectPlantsData,
  (_: RootState, itemName?: UnionGameItemType): UnionGameItemType | undefined =>
    itemName,
  (plant, itemName): number | undefined => {
    if (plant && itemName) {
      return plant[itemName] ? plant[itemName].value : 0;
    }
  },
);

const selectTaskByCurrentBuilds = createSelector(
  (state: RootState): User | null => state.user.user,
  (user): NextBuild | undefined => {
    if (user?.plants && user?.nextBuild) {
      const taskList = user.nextBuild.filter(
        (build) =>
          !Object.entries(user.plants).some(
            ([name, { value }]) => name === build.plant && build.level <= value,
          ),
      );

      return taskList[0];
    }
  },
);

const selectIntroTaskBuilds = createSelector(
  (state: RootState): User | null => state.user.user,
  (user): NextBuild[] | undefined => {
    if (user?.plants && user?.nextBuild) {
      return filterTargetBuilds(user.nextBuild);
    }
  },
);

const selectCoinDepositList = createSelector(
  (state: RootState): TgemCost | undefined => state.user.extendedInfo?.tgemCost,
  (state: RootState): string[] => state.user.user?.invoices ?? [],
  (
    tgemList: TgemCost | undefined,
    invoices: string[],
  ): { withDiscount: boolean; list: BuyCoinItemProps[] } => {
    const withDiscount = invoices.length === 0;

    if (!tgemList) {
      return { withDiscount, list: [] };
    }

    // 1. Перетворюємо об'єкт у масив та сортуємо за кількістю монет,
    // щоб гарантовано знайти найменший пакет (базовий).
    const sortedEntries = Object.entries(tgemList).sort(
      ([coinA], [coinB]) => Number(coinA) - Number(coinB),
    );

    // 2. Знаходимо базову ціну за 1 монету з найменшого пакету
    // (10 монет = 6 зірок -> 0.6 зірки за монету)
    const baseEntry = sortedEntries[0];
    const baseCoinAmount = new Decimal(baseEntry[0]);
    const basePriceAmount = new Decimal(baseEntry[1].amountStars);
    const basePricePerCoin = basePriceAmount.div(baseCoinAmount);

    const list = sortedEntries.map(([coin, price]) => {
      const coinAmount = Number(coin);

      const result: BuyCoinItemProps = {
        price: price.amountUsd,
        starPrice: price.amountStars,
        coin: coinAmount,
        priceDiscount: price.amountUsd * 2,
        withDiscount,
      };

      if (withDiscount) {
        result.priceDiscount = price.amountUsd * 2;
      } else {
        const currentCoins = new Decimal(coin);
        const currentStarsPrice = new Decimal(price.amountStars);

        // Розрахунок справедливої ціни: скільки це мало б коштувати по базовому тарифу
        const fairPrice = currentCoins.mul(basePricePerCoin);

        // Якщо пакет базовий (або fairPrice <= currentStarsPrice через округлення), знижка 0
        if (fairPrice.lte(currentStarsPrice)) {
          result.itemDiscount = 0;
        } else {
          // Формула: (СправедливаЦіна - РеальнаЦіна) / СправедливаЦіна * 100
          result.itemDiscount = Math.round(
            fairPrice
              .minus(currentStarsPrice)
              .div(fairPrice)
              .mul(100)
              .toNumber(),
          );
        }
      }

      return result;
    });

    return {
      withDiscount,
      list,
    };
  },
);

const selectNextStepPlant = createSelector(
  stepPlantsToGetNextLevel,
  (plants): QuarryNextLevel | undefined =>
    plants
      ?.slice()
      .reverse()
      .find((el) => !el.complete),
);

const checkHoldedBalance = createSelector(holdedBalance, (balance): boolean => {
  if (!balance) return false;
  const { tgem } = balance;

  return tgem > 0;
});

const selectAvailableVipPaidOffers = createSelector(
  (state: RootState): PaidVipCostObjectType | undefined =>
    state.user.extendedInfo?.paidVIPCost,
  (
    paidVipCost,
  ):
    | { days: number; discountPrice: number; starPrice: number }[]
    | undefined => {
    if (paidVipCost) {
      const { availableRegistrationOffer, ...offers } = paidVipCost;
      const result: {
        days: number;
        discountPrice: number;
        starPrice: number;
      }[] = [];

      for (const field in offers) {
        const days = Number(field) as 7 | 30 | 90 | 180;

        result.push({
          days,
          discountPrice: offers[days]?.amountUsd || 0,
          starPrice: offers[days]?.amountStars || 0,
        });
      }

      return result;
    }
  },
);

const selectDailyTgemBonus = createSelector(
  (state: RootState): string[] | undefined =>
    state.user.extendedInfo?.dailyTgemsBonus,
  (dailyBonusList): { free: boolean; advertising: boolean } | undefined => {
    if (dailyBonusList) {
      return {
        free: !dailyBonusList.includes('free'),
        advertising: !dailyBonusList.includes('advertising'),
      };
    }
  },
);

const selectHoldedBalanceByItems = createSelector(
  holdedBalance,
  (_: RootState, itemsForFilter: BalanceItem[]): BalanceItem[] =>
    itemsForFilter,
  (holdedBalance, itemsForFilter): FilteredHoldedBalanceItem[] | undefined => {
    if (holdedBalance) {
      return itemsForFilter.map((name) => ({
        name,
        value:
          name in holdedBalance
            ? (holdedBalance[name as keyof typeof holdedBalance] as number) || 0
            : 0,
      }));
    }
  },
);

const selectTutorialByKind = createSelector(
  (state: RootState): Record<SpecContractType, boolean> => state.user.tutorial,
  (
    _: RootState,
    kind: SpecContractType | PaidSpecContractType,
  ): SpecContractType | PaidSpecContractType => kind,
  (tutorial, kind): boolean =>
    kind in tutorial ? tutorial[kind as keyof typeof tutorial] : true,
);

const selectShortLotteryInfoByTier = createSelector(
  (state: RootState): ShortLotterySeasonInfo[] | undefined =>
    state.user.extendedInfo?.nearestLotterySeason,
  (_: RootState, tier: LotteryTicketType): LotteryTicketType => tier,
  (lotteriesInfo, tierName): ShortLotterySeasonInfo | undefined =>
    lotteriesInfo?.find(({ tier }) => tier === tierName),
);

export {
  checkHoldedBalance,
  selectAvailableVipPaidOffers,
  selectBuildLevelByName,
  selectCoinDepositList,
  selectDailyTgemBonus,
  selectHoldedBalanceByItems,
  selectIntroTaskBuilds,
  selectIsGameItemHasOverflow,
  selectNextStepPlant,
  selectResourceStorageByName,
  selectShortLotteryInfoByTier,
  selectStorageItemBySample,
  selectTaskByCurrentBuilds,
  selectTutorialByKind,
};
